import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { MultiValue } from 'react-select';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import BreadCrumbs from './BreadCrumbs';
import { SelectOption } from './SIngleSelectAsync';
import { MultiSelectAsync } from './MultiSelectAsync';
import { prodApiEndpointUrl, stagingApiEndpointUrl, devApiEndpointUrl } from 'types/apiEndpoints';

interface ReportDocument {
  salesforce_report_document_id: string;
  entity_id: number | null;
  entity_name: string | null;
  entity_government_type: string | null;
  report_type_standardized: string | null;
  report_year: number | null;
  report_status: string | null;
  public_url: string | null;
  upload_date?: string | null;
}

interface SearchFacets {
  government_types: string[];
  report_types: string[];
  years: number[];
  total_reports: number;
  total_entities: number;
}

interface SearchResponse {
  results: ReportDocument[];
  total_count: number;
  page: number;
  page_size: number;
}

interface Suggestions {
  entities: { entity_id: number; entity_name: string }[];
  report_types: string[];
  government_types: string[];
}

interface EntityListItem {
  entity_id: string;
  entity_name: string;
  transparency_id: number | null;
}

interface ContentChunk {
  chunk_id: string;
  chunk_text: string;
  score: number;
}

interface ContentResult extends ReportDocument {
  chunks: ContentChunk[];
  total_matches: number;
}

interface ContentSearchResponse {
  results: ContentResult[];
  total_count: number;
  page: number;
  page_size: number;
}

type SortKey = 'entity_name' | 'report_type_standardized' | 'report_year' | 'upload_date';
type SortDir = 'asc' | 'desc';
type SearchMode = 'meta' | 'content';

// Sort options for content mode. Relevance is the default and has no direction toggle.
type ContentSortKey = 'relevance' | 'entity_name' | 'report_year' | 'report_type_standardized';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;
const SUGGEST_DEBOUNCE_MS = 200;
const SNIPPET_CONTEXT_CHARS = 180;

const parseListParam = (v: string | null): string[] =>
  v ? v.split(',').filter(Boolean) : [];

const parseNumberListParam = (v: string | null): number[] =>
  v ? v.split(',').map(Number).filter((n) => !Number.isNaN(n)) : [];

const joinParam = (arr: (string | number)[]): string => arr.join(',');

const GOV_TYPE_COLOR_MAP: Record<string, string> = {
  'City': 'teal',
  'Town': 'teal',
  'County': 'amber',
  'School District or Charter School': 'indigo',
  'Institution of Higher Education': 'indigo',
  'Local and Special Service District': 'slate',
  'Interlocal': 'slate',
  'Conservation District': 'slate',
  'Redevelopment Agency/Project Area': 'purple',
  'Community Reinvestment Agency': 'purple',
  'State of Utah (agencies/depts/comp units/ etc.)': 'amber',
  'AOG': 'slate',
  'Independent/Quasi State Entity': 'slate',
  'Housing': 'purple',
  'Mental Health': 'indigo',
  'Health Provider': 'indigo',
  'District Health': 'indigo',
  'Educational Foundation or Component Unit': 'indigo',
  'Financial Institution': 'slate',
  'CPA Firm': 'slate',
  'Non Profits': 'amber',
  'Non Profit - URS Contributing': 'amber',
  'Component': 'slate',
  'Community User': 'gray',
};

const getGovTypeColor = (govType: string | null | undefined): string => {
  if (!govType) return 'gray';
  return GOV_TYPE_COLOR_MAP[govType] || 'gray';
};

const HIDDEN_ENTITY_NAMES = new Set([
  'test city 2',
  'test anonymous 2',
  'treasurer test',
  'test school',
  'test university',
  'test ifd',
  'test pid',
  'anonymous',
  'my account',
  'salesforce.com',
]);

const isHiddenEntity = (name: string): boolean =>
  HIDDEN_ENTITY_NAMES.has(name.trim().toLowerCase());

const displayText = (value: string | null | undefined, fallback = '—'): string => {
  if (!value || value === 'NULL' || !value.trim()) return fallback;
  return value;
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// upload_date is stored as a 'YYYY-MM-DD' string in Spanner.
const formatUploadDate = (value: string | null | undefined): string => {
  if (!value || value === 'NULL' || !value.trim()) return '—';
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return '—';
  return `${MONTH_SHORT[month - 1]} ${day}, ${year}`;
};

// Very common words that shouldn't be highlighted in snippets even if the user types them.
const HIGHLIGHT_STOPWORDS = new Set([
  'the', 'and', 'for', 'are', 'was', 'were', 'with', 'from', 'into', 'that',
  'this', 'these', 'those', 'has', 'have', 'had', 'but', 'not', 'all', 'any',
]);

// Split user input into the terms we should actually highlight in snippets.
const getHighlightTerms = (keyword: string): string[] => {
  if (!keyword) return [];
  const terms = keyword
    .toLowerCase()
    .split(/[\s+,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3 && !HIGHLIGHT_STOPWORDS.has(t));
  return Array.from(new Set(terms));
};

// Find the earliest position in text where any of the highlight terms appears.
// Used to center the snippet window on a match rather than always starting at 0.
const findFirstMatchIndex = (text: string, terms: string[]): number => {
  if (!terms.length) return 0;
  const lowered = text.toLowerCase();
  let earliest = -1;
  for (const term of terms) {
    const idx = lowered.indexOf(term);
    if (idx !== -1 && (earliest === -1 || idx < earliest)) earliest = idx;
  }
  return earliest === -1 ? 0 : earliest;
};

// Build a short window of text around the first match, with leading/trailing ellipses.
const makeSnippetWindow = (text: string, terms: string[]): string => {
  if (!text) return '';
  if (text.length <= SNIPPET_CONTEXT_CHARS * 2) return text;

  const matchIdx = findFirstMatchIndex(text, terms);
  const halfWindow = SNIPPET_CONTEXT_CHARS;
  let start = Math.max(0, matchIdx - halfWindow);
  let end = Math.min(text.length, matchIdx + halfWindow);

  // Try to snap start/end to word boundaries so we don't chop mid-word.
  if (start > 0) {
    const nextSpace = text.indexOf(' ', start);
    if (nextSpace !== -1 && nextSpace - start < 20) start = nextSpace + 1;
  }
  if (end < text.length) {
    const prevSpace = text.lastIndexOf(' ', end);
    if (prevSpace > start && end - prevSpace < 20) end = prevSpace;
  }

  const leading = start > 0 ? '… ' : '';
  const trailing = end < text.length ? ' …' : '';
  return leading + text.slice(start, end) + trailing;
};

// Render text with matched terms wrapped in <mark>. Case-insensitive.
const HighlightedText: React.FC<{ text: string; terms: string[] }> = ({ text, terms }) => {
  if (!terms.length || !text) return <>{text}</>;

  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        const isMatch = terms.some((t) => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <mark key={i} className="tu-search__mark">{part}</mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        );
      })}
    </>
  );
};

// Content sort dropdown options plus direction labels for each.
const CONTENT_SORT_OPTIONS: {
  key: ContentSortKey;
  label: string;
  defaultDir: SortDir;
  ascLabel: string;
  descLabel: string;
}[] = [
  { key: 'relevance', label: 'Relevance', defaultDir: 'desc', ascLabel: '', descLabel: '' },
  { key: 'entity_name', label: 'Entity', defaultDir: 'asc', ascLabel: 'A to Z', descLabel: 'Z to A' },
  { key: 'report_year', label: 'Year', defaultDir: 'desc', ascLabel: 'Oldest first', descLabel: 'Newest first' },
  { key: 'report_type_standardized', label: 'Report Type', defaultDir: 'asc', ascLabel: 'A to Z', descLabel: 'Z to A' },
];

const SearchReports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const keyword = searchParams.get('q') ?? '';
  const entityIds = parseNumberListParam(searchParams.get('entity'));
  const govTypes = parseListParam(searchParams.get('gov'));
  const reportTypes = parseListParam(searchParams.get('type'));
  const years = parseNumberListParam(searchParams.get('year'));
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const sortBy = (searchParams.get('sort') as SortKey) || 'report_year';
  const sortDir = (searchParams.get('dir') as SortDir) || 'desc';
  const mode: SearchMode = searchParams.get('mode') === 'content' ? 'content' : 'meta';

  // Content-mode sort reads from the same URL params but is its own set of keys.
  // Falls back to 'relevance' when nothing valid is in the URL.
  const contentSortRaw = searchParams.get('sort') as ContentSortKey | null;
  const contentSortBy: ContentSortKey =
    mode === 'content' && contentSortRaw && CONTENT_SORT_OPTIONS.some((o) => o.key === contentSortRaw)
      ? contentSortRaw
      : 'relevance';
  const contentSortDirRaw = (searchParams.get('dir') as SortDir) || null;
  const contentSortOption = CONTENT_SORT_OPTIONS.find((o) => o.key === contentSortBy) ?? CONTENT_SORT_OPTIONS[0];
  const contentSortDir: SortDir = contentSortDirRaw ?? contentSortOption.defaultDir;

  const [keywordInput, setKeywordInput] = useState(keyword);
  const [results, setResults] = useState<ReportDocument[]>([]);
  const [contentResults, setContentResults] = useState<ContentResult[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [facets, setFacets] = useState<SearchFacets>({
    government_types: [],
    report_types: [],
    years: [],
    total_reports: 0,
    total_entities: 0,
  });
  const [allEntities, setAllEntities] = useState<EntityListItem[]>([]);
  const [entityLabelCache, setEntityLabelCache] = useState<Record<number, string>>({});

  const [suggestions, setSuggestions] = useState<Suggestions>({ entities: [], report_types: [], government_types: [] });
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestIdx, setActiveSuggestIdx] = useState(-1);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
  const [sortTooltipOpen, setSortTooltipOpen] = useState(false);

  // To test against my ntgenix-dev backend locally, swap stagingApiEndpointUrl for devApiEndpointUrl on the line below.
  // Swap it back before pushing.
  /* const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
 */
  const [apiEndpoint] = useState<string>(() => {
  const hostname = window.location.hostname;
  if (hostname === "transparent.utah.gov") return prodApiEndpointUrl;
  if (hostname === "localhost" || hostname === "127.0.0.1") return devApiEndpointUrl;
  return stagingApiEndpointUrl;
  });

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string) => {
    return `${apiEndpointUrl}${searchParameter}`;
  }

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const tooltipContainerRef = useRef<HTMLDivElement | null>(null);
  const sortControlRef = useRef<HTMLDivElement | null>(null);

  const highlightTerms = useMemo(() => getHighlightTerms(keyword), [keyword]);

  const updateUrl = useCallback(
    (updates: Record<string, string | number | null>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === '' || v === undefined) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      });
      // Reset page whenever a non-page filter changes
      if (Object.keys(updates).some((k) => k !== 'page')) {
        next.delete('page');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (keywordInput !== keyword) {
        updateUrl({ q: keywordInput || null });
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordInput]);

  useEffect(() => {
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    const q = keywordInput.trim();
    if (!q) {
      setSuggestions({ entities: [], report_types: [], government_types: [] });
      return;
    }
    const controller = new AbortController();
    suggestDebounceRef.current = setTimeout(async () => {
      try {
        const searchSuggestionsEncodedParam = encodeURIComponent(JSON.stringify({ query: q }));
        const searchSuggestionsSearchString = `/?function=getSearchSuggestions&parameter=${searchSuggestionsEncodedParam}`;
        const res = await fetch(
          buildApiUrl(apiEndpoint, searchSuggestionsSearchString),
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSuggestions({
          entities: Array.isArray(data.entities) ? data.entities : [],
          report_types: Array.isArray(data.report_types) ? data.report_types : [],
          government_types: Array.isArray(data.government_types) ? data.government_types : [],
        });
        setActiveSuggestIdx(-1);
      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('Fetch error (suggestions):', err);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
      controller.abort();
    };
  }, [keywordInput]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setSuggestOpen(false);
      }
      if (tooltipContainerRef.current && !tooltipContainerRef.current.contains(e.target as Node)) {
        setOpenTooltipId(null);
      }
      if (sortControlRef.current && !sortControlRef.current.contains(e.target as Node)) {
        setSortTooltipOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const reportSearchFiltersEncodedParam = encodeURIComponent(JSON.stringify({}));
        const reportSearchFiltersSearchString = `/?function=getReportSearchFilters&parameter=${reportSearchFiltersEncodedParam}`;
        const res = await fetch(
          buildApiUrl(apiEndpoint, reportSearchFiltersSearchString),
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!controller.signal.aborted) {
          const govs = (Array.isArray(data.government_types) ? data.government_types : [])
            .filter((g: string) => g && g !== 'NULL');
          setFacets({
            government_types: govs,
            report_types: Array.isArray(data.report_types) ? data.report_types : [],
            years: Array.isArray(data.years) ? data.years : [],
            total_reports: data.total_reports ?? 0,
            total_entities: data.total_entities ?? 0,
          });
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('Fetch error (facets):', err);
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const listOfAllEntitiesEncodedParam = encodeURIComponent(JSON.stringify({}));
        const listOfAllEntitiesSearchString = `/?function=getListOfAllEntities&parameter=${listOfAllEntitiesEncodedParam}`;
        const res = await fetch(
          buildApiUrl(apiEndpoint, listOfAllEntitiesSearchString),
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list: EntityListItem[] = (Array.isArray(data) ? data : [])
          .filter((e: any) => e && e.entity_name && e.transparency_id != null)
          .map((e: any) => ({
            entity_id: String(e.entity_id),
            entity_name: String(e.entity_name),
            transparency_id: Number(e.transparency_id),
          }))
          .filter((e: EntityListItem) => !isHiddenEntity(e.entity_name))
          .sort((a: EntityListItem, b: EntityListItem) =>
            a.entity_name.localeCompare(b.entity_name, undefined, { sensitivity: 'base' })
          );

        if (!controller.signal.aborted) {
          setAllEntities(list);
          setEntityLabelCache((prev) => {
            const next = { ...prev };
            list.forEach((e) => {
              if (e.transparency_id != null) next[e.transparency_id] = e.entity_name;
            });
            return next;
          });
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('Fetch error (entities):', err);
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    if (mode === 'content' && keyword.trim().length < 2) {
      setContentResults([]);
      setResults([]);
      setTotalCount(0);
      setLoading(false);
      return () => controller.abort();
    }

    (async () => {
      try {
        if (mode === 'content') {
          const payload: Record<string, any> = {
            keyword,
            entity_ids: entityIds,
            government_types: govTypes,
            report_types: reportTypes,
            years,
            page,
            page_size: PAGE_SIZE,
          };
          // Only send sort params when the user moved off the default (relevance).
          if (contentSortBy !== 'relevance') {
            payload.sort_by = contentSortBy;
            payload.sort_dir = contentSortDir;
          }
          const searchReportContentEncodedParam = encodeURIComponent(JSON.stringify(payload));
          const searchReportContentSearchString = `/?function=searchReportContent&parameter=${searchReportContentEncodedParam}`;
          const res = await fetch(
            buildApiUrl(apiEndpoint, searchReportContentSearchString),
            { signal: controller.signal }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: ContentSearchResponse = await res.json();
          if (!controller.signal.aborted) {
            const rows = Array.isArray(data.results) ? data.results : [];
            setContentResults(rows);
            setResults([]);
            setTotalCount(data.total_count ?? 0);
            setEntityLabelCache((prev) => {
              const next = { ...prev };
              rows.forEach((r) => {
                if (r.entity_id != null && r.entity_name) next[r.entity_id] = r.entity_name;
              });
              return next;
            });
          }
        } else {
          const payload = {
            keyword,
            entity_ids: entityIds,
            government_types: govTypes,
            report_types: reportTypes,
            years,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            page_size: PAGE_SIZE,
          };
          const searchReportsEncodedParam = encodeURIComponent(JSON.stringify(payload));
          const searchReportsSearchString = `/?function=searchReports&parameter=${searchReportsEncodedParam}`;
          const res = await fetch(
            buildApiUrl(apiEndpoint, searchReportsSearchString),
            { signal: controller.signal }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: SearchResponse = await res.json();
          if (!controller.signal.aborted) {
            const rows = Array.isArray(data.results) ? data.results : [];
            setResults(rows);
            setContentResults([]);
            setTotalCount(data.total_count ?? 0);
            setEntityLabelCache((prev) => {
              const next = { ...prev };
              rows.forEach((r) => {
                if (r.entity_id != null && r.entity_name) next[r.entity_id] = r.entity_name;
              });
              return next;
            });
          }
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('Fetch error (search):', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [
    mode,
    keyword,
    entityIds.join(','),
    govTypes.join(','),
    reportTypes.join(','),
    years.join(','),
    sortBy,
    sortDir,
    contentSortBy,
    contentSortDir,
    page,
  ]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      updateUrl({ dir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      const defaultDirForKey: SortDir =
        key === 'report_year' || key === 'upload_date' ? 'desc' : 'asc';
      updateUrl({ sort: key, dir: defaultDirForKey });
    }
  };

  const handleContentSortChange = (nextKey: ContentSortKey) => {
    if (nextKey === 'relevance') {
      updateUrl({ sort: null, dir: null });
      return;
    }
    const next = CONTENT_SORT_OPTIONS.find((o) => o.key === nextKey);
    if (!next) return;
    updateUrl({ sort: nextKey, dir: next.defaultDir });
  };

  const handleContentSortDirToggle = () => {
    if (contentSortBy === 'relevance') return;
    updateUrl({ dir: contentSortDir === 'asc' ? 'desc' : 'asc' });
  };

  const handleClearAll = () => {
    setKeywordInput('');
    const next = new URLSearchParams();
    if (mode === 'content') next.set('mode', 'content');
    setSearchParams(next, { replace: true });
  };

  const handleMultiChange = (key: 'gov' | 'type' | 'year' | 'entity') =>
    (selected: MultiValue<SelectOption>) => {
      const values = selected.map((s) => s.value);

      if (key === 'entity') {
        setEntityLabelCache((prev) => {
          const next = { ...prev };
          selected.forEach((s) => {
            const id = Number(s.value);
            if (!Number.isNaN(id)) next[id] = s.label;
          });
          return next;
        });
      }

      updateUrl({ [key]: values.length ? joinParam(values) : null });
    };

  const handleModeChange = (nextMode: SearchMode) => {
    if (nextMode === mode) return;
    setOpenTooltipId(null);
    setKeywordInput('');
    const updates: Record<string, string | number | null> = {
      mode: nextMode === 'content' ? 'content' : null,
      q: null,
    };
    if (nextMode === 'content') {
      updates.sort = null;
      updates.dir = null;
    }
    updateUrl(updates);
  };

  // Suggestion click handlers. Each applies a filter and clears the keyword.
  const pickEntitySuggestion = (s: { entity_id: number; entity_name: string }) => {
    setEntityLabelCache((prev) => ({ ...prev, [s.entity_id]: s.entity_name }));
    setKeywordInput('');
    setSuggestOpen(false);
    const merged = Array.from(new Set([...entityIds.map(String), String(s.entity_id)]));
    updateUrl({ q: null, entity: joinParam(merged) });
  };

  const pickReportTypeSuggestion = (rt: string) => {
    setKeywordInput('');
    setSuggestOpen(false);
    const merged = Array.from(new Set([...reportTypes, rt]));
    updateUrl({ q: null, type: joinParam(merged) });
  };

  const pickGovTypeSuggestion = (gt: string) => {
    setKeywordInput('');
    setSuggestOpen(false);
    const merged = Array.from(new Set([...govTypes, gt]));
    updateUrl({ q: null, gov: joinParam(merged) });
  };

  const acceptFreeTextSuggestion = () => {
    setSuggestOpen(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    updateUrl({ q: keywordInput || null });
  };

  const removeFilterChip = (kind: 'q' | 'entity' | 'gov' | 'type' | 'year', value?: string) => {
    if (kind === 'q') {
      setKeywordInput('');
      updateUrl({ q: null });
      return;
    }
    if (kind === 'entity' && value) {
      const next = entityIds.filter((id) => String(id) !== value);
      updateUrl({ entity: next.length ? joinParam(next) : null });
      return;
    }
    if (kind === 'gov' && value) {
      const next = govTypes.filter((g) => g !== value);
      updateUrl({ gov: next.length ? joinParam(next) : null });
      return;
    }
    if (kind === 'type' && value) {
      const next = reportTypes.filter((t) => t !== value);
      updateUrl({ type: next.length ? joinParam(next) : null });
      return;
    }
    if (kind === 'year' && value) {
      const next = years.filter((y) => String(y) !== value);
      updateUrl({ year: next.length ? joinParam(next) : null });
      return;
    }
  };

  const govOptions: SelectOption[] = useMemo(
    () => facets.government_types.map((g) => ({ value: g, label: g })),
    [facets.government_types]
  );
  const typeOptions: SelectOption[] = useMemo(
    () => facets.report_types.map((t) => ({ value: t, label: t })),
    [facets.report_types]
  );
  const yearOptions: SelectOption[] = useMemo(
    () => facets.years.map((y) => ({ value: String(y), label: String(y) })),
    [facets.years]
  );

  const selectedGovValues: SelectOption[] = useMemo(
    () => govTypes.map((g) => ({ value: g, label: g })),
    [govTypes]
  );
  const selectedTypeValues: SelectOption[] = useMemo(
    () => reportTypes.map((t) => ({ value: t, label: t })),
    [reportTypes]
  );
  const selectedYearValues: SelectOption[] = useMemo(
    () => years.map((y) => ({ value: String(y), label: String(y) })),
    [years]
  );
  const selectedEntityValues: SelectOption[] = useMemo(
    () => entityIds.map((id) => ({
      value: String(id),
      label: entityLabelCache[id] || `Entity #${id}`,
    })),
    [entityIds, entityLabelCache]
  );

  const hasActiveFilters =
    keyword.length > 0 ||
    entityIds.length > 0 ||
    govTypes.length > 0 ||
    reportTypes.length > 0 ||
    years.length > 0;

  const activeFilterCount =
    (keyword ? 1 : 0) +
    entityIds.length +
    govTypes.length +
    reportTypes.length +
    years.length;

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startRow = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRow = Math.min(page * PAGE_SIZE, totalCount);

  // Entity filter dropdown: client-side filter against preloaded allEntities.
  const loadEntityOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
  const q = input.toLowerCase();
  return allEntities
    .filter((e) => e.entity_name.toLowerCase().includes(q))
    .map((e) => ({
      value: String(e.transparency_id),
      label: e.entity_name,
    }));
  }, [allEntities]);

  const defaultEntityOptions: SelectOption[] = useMemo(
  () => allEntities.map((e) => ({
    value: String(e.transparency_id),
    label: e.entity_name,
  })),
  [allEntities]
  );

  const sortIndicator = (key: SortKey) => {
    if (sortBy !== key) return <i className="fas fa-sort tu-search__sortIcon tu-search__sortIcon--inactive" aria-hidden="true"></i>;
    return (
      <i
        className={`fas fa-sort-${sortDir === 'asc' ? 'up' : 'down'} tu-search__sortIcon`}
        aria-hidden="true"
      ></i>
    );
  };

  // Flat list of all suggestion "items" for keyboard nav. Order matters here.
  const flatSuggestions = useMemo(() => {
    const list: { type: 'entity' | 'report_type' | 'gov_type' | 'freetext'; value: any }[] = [];
    suggestions.entities.forEach((e) => list.push({ type: 'entity', value: e }));
    suggestions.report_types.forEach((t) => list.push({ type: 'report_type', value: t }));
    suggestions.government_types.forEach((g) => list.push({ type: 'gov_type', value: g }));
    if (keywordInput.trim()) list.push({ type: 'freetext', value: keywordInput });
    return list;
  }, [suggestions, keywordInput]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestOpen || flatSuggestions.length === 0) {
      if (e.key === 'Enter') {
        acceptFreeTextSuggestion();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestIdx((i) => Math.min(i + 1, flatSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestIdx < 0) {
        acceptFreeTextSuggestion();
        return;
      }
      const picked = flatSuggestions[activeSuggestIdx];
      if (!picked) return;
      if (picked.type === 'entity') pickEntitySuggestion(picked.value);
      else if (picked.type === 'report_type') pickReportTypeSuggestion(picked.value);
      else if (picked.type === 'gov_type') pickGovTypeSuggestion(picked.value);
      else if (picked.type === 'freetext') acceptFreeTextSuggestion();
    } else if (e.key === 'Escape') {
      setSuggestOpen(false);
    } else if (e.key === 'Tab') {
      setSuggestOpen(false);
    }
  };

  const showSuggestDropdown =
    mode === 'meta' &&
    suggestOpen &&
    keywordInput.trim().length > 0 &&
    flatSuggestions.length > 0;

  let suggestIdxCounter = -1;
  const nextSuggestIdx = () => ++suggestIdxCounter;

  const searchPlaceholder = mode === 'content'
    ? 'Search for words inside reports (e.g. water, pension, overtime)...'
    : 'Search entities, report types, years...';

  const helperText = mode === 'content'
    ? "You're searching the actual words written inside each report. This is still being rolled out, so some reports may not yet be searchable this way."
    : "You're searching across all reports by the entity that published them, the type of report, the year, or the kind of government.";

  const tooltips: Record<SearchMode, string> = {
    meta: 'Find reports by the name of the entity that published them, the type of report (like Budget or Audit), the year, or the kind of government (like City or County). This is the fastest way to browse our full library.',
    content: 'Find reports that contain specific words inside their pages, not just in their title. For example, search "water" to find reports that talk about water anywhere in the document. This feature is still being rolled out, so not every report is searchable this way yet.',
  };

  const toggleTooltip = (id: string) => {
    setOpenTooltipId((cur) => (cur === id ? null : id));
  };

  const directionLabel =
    contentSortDir === 'asc' ? contentSortOption.ascLabel : contentSortOption.descLabel;
  const showDirectionButton = mode === 'content' && contentSortBy !== 'relevance';

  return (
    <div className="dashboard">
      <FeatureNav />
      <BreadCrumbs />

      <div className="tu-searchHero">
        <Container>
          <div className="tu-searchHero__card">
            <div className="tu-searchHero__accentBar" aria-hidden="true"></div>

            <div className="tu-searchHero__inner">
              <h1 className="tu-searchHero__title">Search Reports</h1>
              <p className="tu-searchHero__subtitle">
                Search and download financial reports published by Utah government entities.
              </p>

              {mode === 'meta' && facets.total_reports > 0 && (
                <div className="tu-searchHero__stats">
                  Searching{' '}
                  <strong>{facets.total_reports.toLocaleString()}</strong> approved reports
                  {' '}across{' '}
                  <strong>{facets.total_entities.toLocaleString()}</strong> entities
                </div>
              )}

              <div className="tu-searchHero__searchBox" ref={searchBoxRef}>
                <i className="fas fa-search tu-searchHero__searchIcon" aria-hidden="true"></i>
                <input
                  type="text"
                  className="tu-searchHero__searchInput"
                  placeholder={searchPlaceholder}
                  value={keywordInput}
                  onChange={(e) => {
                    setKeywordInput(e.target.value);
                    if (mode === 'meta') setSuggestOpen(true);
                  }}
                  onFocus={() => {
                    if (mode === 'meta' && keywordInput.trim()) setSuggestOpen(true);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Search reports"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestDropdown}
                  aria-controls="tu-search-suggest-list"
                />
                {keywordInput && (
                  <button
                    type="button"
                    className="tu-searchHero__searchClear"
                    onClick={() => {
                      setKeywordInput('');
                      setSuggestOpen(false);
                      updateUrl({ q: null });
                    }}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                  </button>
                )}

                {showSuggestDropdown && (
                  <div
                    className="tu-suggest"
                    id="tu-search-suggest-list"
                    role="listbox"
                  >
                    {suggestions.entities.length > 0 && (
                      <div className="tu-suggest__group">
                        <div className="tu-suggest__groupLabel">Entities</div>
                        {suggestions.entities.map((e) => {
                          const idx = nextSuggestIdx();
                          const active = idx === activeSuggestIdx;
                          return (
                            <button
                              type="button"
                              key={`ent-${e.entity_id}`}
                              className={`tu-suggest__item${active ? ' tu-suggest__item--active' : ''}`}
                              onClick={() => pickEntitySuggestion(e)}
                              onMouseEnter={() => setActiveSuggestIdx(idx)}
                              role="option"
                              aria-selected={active}
                            >
                              <i className="fas fa-landmark tu-suggest__icon" aria-hidden="true"></i>
                              <span className="tu-suggest__text">{e.entity_name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {suggestions.report_types.length > 0 && (
                      <div className="tu-suggest__group">
                        <div className="tu-suggest__groupLabel">Report Types</div>
                        {suggestions.report_types.map((t) => {
                          const idx = nextSuggestIdx();
                          const active = idx === activeSuggestIdx;
                          return (
                            <button
                              type="button"
                              key={`rt-${t}`}
                              className={`tu-suggest__item${active ? ' tu-suggest__item--active' : ''}`}
                              onClick={() => pickReportTypeSuggestion(t)}
                              onMouseEnter={() => setActiveSuggestIdx(idx)}
                              role="option"
                              aria-selected={active}
                            >
                              <i className="fas fa-file-alt tu-suggest__icon" aria-hidden="true"></i>
                              <span className="tu-suggest__text">{t}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {suggestions.government_types.length > 0 && (
                      <div className="tu-suggest__group">
                        <div className="tu-suggest__groupLabel">Government Types</div>
                        {suggestions.government_types.map((g) => {
                          const idx = nextSuggestIdx();
                          const active = idx === activeSuggestIdx;
                          return (
                            <button
                              type="button"
                              key={`gt-${g}`}
                              className={`tu-suggest__item${active ? ' tu-suggest__item--active' : ''}`}
                              onClick={() => pickGovTypeSuggestion(g)}
                              onMouseEnter={() => setActiveSuggestIdx(idx)}
                              role="option"
                              aria-selected={active}
                            >
                              <i className="fas fa-building tu-suggest__icon" aria-hidden="true"></i>
                              <span className="tu-suggest__text">{g}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {keywordInput.trim() && (() => {
                      const idx = nextSuggestIdx();
                      const active = idx === activeSuggestIdx;
                      return (
                        <button
                          type="button"
                          className={`tu-suggest__freetext${active ? ' tu-suggest__freetext--active' : ''}`}
                          onClick={acceptFreeTextSuggestion}
                          onMouseEnter={() => setActiveSuggestIdx(idx)}
                          role="option"
                          aria-selected={active}
                        >
                          <i className="fas fa-search tu-suggest__icon" aria-hidden="true"></i>
                          <span className="tu-suggest__text">
                            Search reports for <strong>"{keywordInput}"</strong>
                          </span>
                          <i className="fas fa-level-down-alt tu-suggest__enterHint" aria-hidden="true"></i>
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="tu-modeToggle" ref={tooltipContainerRef} role="radiogroup" aria-label="Search mode">
                <button
                  type="button"
                  className={`tu-modeToggle__btn${mode === 'meta' ? ' tu-modeToggle__btn--active' : ''}`}
                  onClick={() => handleModeChange('meta')}
                  role="radio"
                  aria-checked={mode === 'meta'}
                >
                  <span className="tu-modeToggle__radio" aria-hidden="true">
                    {mode === 'meta' && <span className="tu-modeToggle__radioInner" />}
                  </span>
                  <span className="tu-modeToggle__label">Search reports</span>
                  <span
                    className="tu-modeToggle__info"
                    role="button"
                    tabIndex={0}
                    aria-label="About searching reports"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTooltip('meta');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleTooltip('meta');
                      }
                    }}
                  >
                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                    <span className={`tu-modeToggle__tooltip${openTooltipId === 'meta' ? ' tu-modeToggle__tooltip--open' : ''}`}>
                      {tooltips.meta}
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  className={`tu-modeToggle__btn${mode === 'content' ? ' tu-modeToggle__btn--active' : ''}`}
                  onClick={() => handleModeChange('content')}
                  role="radio"
                  aria-checked={mode === 'content'}
                >
                  <span className="tu-modeToggle__radio" aria-hidden="true">
                    {mode === 'content' && <span className="tu-modeToggle__radioInner" />}
                  </span>
                  <span className="tu-modeToggle__label">Search inside reports</span>
                  <span
                    className="tu-modeToggle__info"
                    role="button"
                    tabIndex={0}
                    aria-label="About searching inside reports"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTooltip('content');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleTooltip('content');
                      }
                    }}
                  >
                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                    <span className={`tu-modeToggle__tooltip${openTooltipId === 'content' ? ' tu-modeToggle__tooltip--open' : ''}`}>
                      {tooltips.content}
                    </span>
                  </span>
                </button>
              </div>

              <p className="tu-modeToggle__helper">{helperText}</p>

              <button
                type="button"
                className="tu-searchHero__mobileToggle"
                onClick={() => setMobileFiltersOpen((o) => !o)}
                aria-expanded={mobileFiltersOpen}
              >
                <i className="fas fa-sliders-h" aria-hidden="true"></i>
                &nbsp;&nbsp;Filters
                {activeFilterCount > 0 && (
                  <span className="tu-searchHero__mobileCount">{activeFilterCount}</span>
                )}
                <i className={`fas fa-chevron-${mobileFiltersOpen ? 'up' : 'down'} tu-searchHero__mobileChevron`} aria-hidden="true"></i>
              </button>

              <div className={`tu-searchHero__filters${mobileFiltersOpen ? ' tu-searchHero__filters--open' : ''}`}>
                <div className="tu-searchHero__filterGrid">

                  <div className="tu-searchHero__filterGroup">
                    <div className="tu-searchHero__filterGroup__hint">
                      <i className="fas fa-info-circle" aria-hidden="true"></i>
                      <span>Match by entity, by government type, or both</span>
                    </div>

                    <div className="tu-searchHero__filterGroup__fields">
                      <div className="tu-searchHero__filterField tu-searchHero__filterField--primary">
                        <label className="tu-searchHero__filterLabel">
                          <i className="fas fa-landmark" aria-hidden="true"></i>&nbsp;&nbsp;Entity
                        </label>
                        <MultiSelectAsync
                          value={selectedEntityValues}
                          onChange={handleMultiChange('entity')}
                          isSearchable
                          loadOptions={loadEntityOptions}
                          defaultOptions={defaultEntityOptions}
                          options={defaultEntityOptions}
                        />
                      </div>

                      <div className="tu-searchHero__orConnector" aria-hidden="true">
                        <span className="tu-searchHero__orConnector__pill">or</span>
                      </div>

                      <div className="tu-searchHero__filterField">
                        <label className="tu-searchHero__filterLabel">
                          <i className="fas fa-building" aria-hidden="true"></i>&nbsp;&nbsp;Government Type
                        </label>
                        <MultiSelectAsync
                          value={selectedGovValues}
                          onChange={handleMultiChange('gov')}
                          isSearchable
                          loadOptions={async (input) =>
                            govOptions.filter((o) => o.label.toLowerCase().includes(input.toLowerCase()))
                          }
                          defaultOptions={govOptions}
                          options={govOptions}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="tu-searchHero__filterField">
                    <label className="tu-searchHero__filterLabel">
                      <i className="fas fa-file-alt" aria-hidden="true"></i>&nbsp;&nbsp;Report Type
                    </label>
                    <MultiSelectAsync
                      value={selectedTypeValues}
                      onChange={handleMultiChange('type')}
                      isSearchable
                      loadOptions={async (input) =>
                        typeOptions.filter((o) => o.label.toLowerCase().includes(input.toLowerCase()))
                      }
                      defaultOptions={typeOptions}
                      options={typeOptions}
                    />
                  </div>

                  <div className="tu-searchHero__filterField">
                    <label className="tu-searchHero__filterLabel">
                      <i className="fas fa-calendar" aria-hidden="true"></i>&nbsp;&nbsp;Year
                    </label>
                    <MultiSelectAsync
                      value={selectedYearValues}
                      onChange={handleMultiChange('year')}
                      isSearchable
                      loadOptions={async (input) =>
                        yearOptions.filter((o) => o.label.includes(input))
                      }
                      defaultOptions={yearOptions}
                      options={yearOptions}
                    />
                  </div>
                </div>

                {hasActiveFilters && (
                  <div className="tu-searchHero__filterActions">
                    <button
                      type="button"
                      className="tu-searchHero__clearBtn"
                      onClick={handleClearAll}
                    >
                      <i className="fas fa-times-circle" aria-hidden="true"></i>
                      &nbsp;&nbsp;Clear all filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container className="mt-4 mb-5">
        <div className="tu-search__meta">
          <div className="tu-search__metaLeft">
            {loading ? (
              <span className="tu-search__metaLoading">
                <span className="tu-entityDash__spinner" style={{ width: 18, height: 18, borderWidth: 2 }}></span>
                &nbsp;&nbsp;Searching...
              </span>
            ) : (
              <span className="tu-search__metaCount">
                <strong>{totalCount.toLocaleString()}</strong>
                {totalCount === 1 ? ' report' : ' reports'} found
                {totalCount > 0 && (
                  <span className="tu-search__metaRange">
                    <span className="tu-search__metaSep"> · </span>
                    Showing {startRow.toLocaleString()}–{endRow.toLocaleString()}
                  </span>
                )}
              </span>
            )}

            {hasActiveFilters && (
              <div className="tu-search__chips">
                {keyword && (
                  <span className="tu-search__chip tu-search__chip--gray">
                    <span className="tu-search__chipLabel">Keyword:</span>
                    <span className="tu-search__chipValue">{keyword}</span>
                    <button
                      type="button"
                      className="tu-search__chipRemove"
                      onClick={() => removeFilterChip('q')}
                      aria-label={`Remove keyword ${keyword}`}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </span>
                )}
                {entityIds.map((id) => {
                  const label = entityLabelCache[id] || `Entity #${id}`;
                  return (
                    <span key={`chip-entity-${id}`} className="tu-search__chip tu-search__chip--teal">
                      {label}
                      <button
                        type="button"
                        className="tu-search__chipRemove"
                        onClick={() => removeFilterChip('entity', String(id))}
                        aria-label={`Remove entity ${label}`}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </button>
                    </span>
                  );
                })}
                {govTypes.map((g) => (
                  <span
                    key={`chip-gov-${g}`}
                    className={`tu-search__chip tu-search__chip--${getGovTypeColor(g)}`}
                  >
                    {g}
                    <button
                      type="button"
                      className="tu-search__chipRemove"
                      onClick={() => removeFilterChip('gov', g)}
                      aria-label={`Remove government type ${g}`}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </span>
                ))}
                {reportTypes.map((t) => (
                  <span key={`chip-type-${t}`} className="tu-search__chip tu-search__chip--amber">
                    {t}
                    <button
                      type="button"
                      className="tu-search__chipRemove"
                      onClick={() => removeFilterChip('type', t)}
                      aria-label={`Remove report type ${t}`}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </span>
                ))}
                {years.map((y) => (
                  <span key={`chip-year-${y}`} className="tu-search__chip tu-search__chip--slate">
                    {y}
                    <button
                      type="button"
                      className="tu-search__chipRemove"
                      onClick={() => removeFilterChip('year', String(y))}
                      aria-label={`Remove year ${y}`}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {mode === 'content' && (
            <div className="tu-search__sortControl" ref={sortControlRef}>
              <span className="tu-search__sortLabel">Sort by:</span>
              <span
                className="tu-search__sortInfo"
                role="button"
                tabIndex={0}
                aria-label="About sorting"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortTooltipOpen((o) => !o);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setSortTooltipOpen((o) => !o);
                  }
                }}
              >
                <i className="fas fa-info-circle" aria-hidden="true"></i>
                <span className={`tu-search__sortTooltip${sortTooltipOpen ? ' tu-search__sortTooltip--open' : ''}`}>
                  Sorting changes the order of results. "Relevance" puts the strongest matches first. The other options sort by entity name, report year, or report type. Use the arrow next to your choice to switch between A → Z and Z → A, or newest and oldest.
                </span>
              </span>

              <div className="tu-search__sortSelectWrap">
                <select
                  className="tu-search__sortSelect"
                  value={contentSortBy}
                  onChange={(e) => handleContentSortChange(e.target.value as ContentSortKey)}
                  aria-label="Sort results by"
                >
                  {CONTENT_SORT_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <i className="fas fa-chevron-down tu-search__sortSelectChevron" aria-hidden="true"></i>
              </div>

              {showDirectionButton && (
                <button
                  type="button"
                  className="tu-search__sortDirBtn"
                  onClick={handleContentSortDirToggle}
                  aria-label={`Change direction. Currently ${directionLabel.toLowerCase()}.`}
                  title="Change direction"
                >
                  <i
                    className={`fas fa-arrow-${contentSortDir === 'asc' ? 'up' : 'down'}`}
                    aria-hidden="true"
                  ></i>
                  <span className="tu-search__sortDirLabel">{directionLabel}</span>
                </button>
              )}
            </div>
          )}
        </div>

        {mode === 'content' ? (
          <ContentResultsView
            loading={loading}
            keyword={keyword}
            results={contentResults}
            highlightTerms={highlightTerms}
            hasActiveFilters={hasActiveFilters}
            onClearAll={handleClearAll}
            onSwitchToMeta={() => handleModeChange('meta')}
          />
        ) : (
          <MetaResultsView
            loading={loading}
            results={results}
            hasActiveFilters={hasActiveFilters}
            onClearAll={handleClearAll}
            onSort={handleSort}
            sortIndicator={sortIndicator}
          />
        )}

        {totalCount > PAGE_SIZE && (
          <div className="tu-search__pagination">
            <button
              type="button"
              className="tu-search__pageBtn"
              disabled={page <= 1 || loading}
              onClick={() => updateUrl({ page: page - 1 })}
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
              &nbsp;&nbsp;Previous
            </button>

            <span className="tu-search__pageInfo">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>

            <button
              type="button"
              className="tu-search__pageBtn"
              disabled={page >= totalPages || loading}
              onClick={() => updateUrl({ page: page + 1 })}
            >
              Next&nbsp;&nbsp;
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </Container>

      <FooterSection />
    </div>
  );
};

// Metadata results (table)

interface MetaResultsViewProps {
  loading: boolean;
  results: ReportDocument[];
  hasActiveFilters: boolean;
  onClearAll: () => void;
  onSort: (key: SortKey) => void;
  sortIndicator: (key: SortKey) => React.ReactNode;
}

const MetaResultsView: React.FC<MetaResultsViewProps> = ({
  loading,
  results,
  hasActiveFilters,
  onClearAll,
  onSort,
  sortIndicator,
}) => {
  return (
    <div className="tu-reports__tableWrap tu-search__tableWrap">
      <table className="table tu-reports__table tu-search__table">
        <thead>
          <tr>
            <th onClick={() => onSort('entity_name')} className="tu-search__sortable" scope="col">
              Entity {sortIndicator('entity_name')}
            </th>
            <th scope="col">Government Type</th>
            <th onClick={() => onSort('report_type_standardized')} className="tu-search__sortable" scope="col">
              Report Type {sortIndicator('report_type_standardized')}
            </th>
            <th onClick={() => onSort('report_year')} className="tu-search__sortable tu-col-year" scope="col">
              Year {sortIndicator('report_year')}
            </th>
            <th onClick={() => onSort('upload_date')} className="tu-search__sortable tu-col-uploaded" scope="col">
              Upload Date {sortIndicator('upload_date')}
            </th>
            <th scope="col" className="tu-col-action">Download</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="tu-search__skeletonRow">
                <td><span className="tu-search__skeleton tu-search__skeleton--md"></span></td>
                <td><span className="tu-search__skeleton tu-search__skeleton--sm"></span></td>
                <td><span className="tu-search__skeleton tu-search__skeleton--md"></span></td>
                <td className="tu-col-year"><span className="tu-search__skeleton tu-search__skeleton--xs"></span></td>
                <td className="tu-col-uploaded"><span className="tu-search__skeleton tu-search__skeleton--sm"></span></td>
                <td className="tu-col-action"><span className="tu-search__skeleton tu-search__skeleton--btn"></span></td>
              </tr>
            ))
          ) : results.length === 0 ? (
            <tr>
              <td colSpan={6} className="tu-reports__empty">
                <div className="tu-search__emptyState">
                  <i className="fas fa-search tu-search__emptyIcon" aria-hidden="true"></i>
                  <div className="tu-search__emptyTitle">No approved reports match your search</div>
                  <div className="tu-search__emptyBody">
                    Try removing a filter or broadening your keyword.
                    {hasActiveFilters && (
                      <>
                        {' '}
                        <button type="button" className="tu-search__emptyLink" onClick={onClearAll}>
                          Clear all filters
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            results.map((r) => {
              const govType = r.entity_government_type;
              const hasGovType = !!govType && govType !== 'NULL' && govType.trim() !== '';
              return (
                <tr key={r.salesforce_report_document_id}>
                  <td className="tu-reports__primary">{displayText(r.entity_name)}</td>
                  <td>
                    {hasGovType ? (
                      <span className={`tu-search__govChip tu-search__govChip--${getGovTypeColor(govType)}`}>
                        {govType}
                      </span>
                    ) : (
                      <span className="tu-muted">—</span>
                    )}
                  </td>
                  <td>{displayText(r.report_type_standardized)}</td>
                  <td className="tu-col-year">{r.report_year ?? '—'}</td>
                  <td className="tu-col-uploaded">{formatUploadDate(r.upload_date)}</td>
                  <td className="tu-col-action">
                    {r.public_url ? (
                      <a href={r.public_url} target="_blank" rel="noopener noreferrer" className="tu-link">
                        <i className="fas fa-download" aria-hidden="true"></i>
                        &nbsp;&nbsp;Download
                      </a>
                    ) : (
                      <span className="tu-muted">Unavailable</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

// Content results (cards with snippets)

interface ContentResultsViewProps {
  loading: boolean;
  keyword: string;
  results: ContentResult[];
  highlightTerms: string[];
  hasActiveFilters: boolean;
  onClearAll: () => void;
  onSwitchToMeta: () => void;
}

const ContentResultsView: React.FC<ContentResultsViewProps> = ({
  loading,
  keyword,
  results,
  highlightTerms,
  hasActiveFilters,
  onClearAll,
  onSwitchToMeta,
}) => {
  if (loading) {
    return (
      <div className="tu-search__contentList">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`content-skel-${i}`} className="tu-search__contentCard tu-search__contentCard--loading">
            <div className="tu-search__contentHead">
              <span className="tu-search__skeleton tu-search__skeleton--md" />
              <span className="tu-search__skeleton tu-search__skeleton--xs" />
            </div>
            <span className="tu-search__skeleton tu-search__skeleton--full" />
            <span className="tu-search__skeleton tu-search__skeleton--full" />
            <span className="tu-search__skeleton tu-search__skeleton--md" />
          </div>
        ))}
      </div>
    );
  }

  if (!keyword.trim()) {
    return (
      <div className="tu-search__contentEmpty">
        <i className="fas fa-file-alt tu-search__emptyIcon" aria-hidden="true"></i>
        <div className="tu-search__emptyTitle">Type something in the search bar to search inside reports</div>
        <div className="tu-search__emptyBody">
          Try a word like "water", "pension", or "overtime" to find reports that mention it.
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="tu-search__contentEmpty">
        <i className="fas fa-search tu-search__emptyIcon" aria-hidden="true"></i>
        <div className="tu-search__emptyTitle">We couldn't find any reports that mention those words.</div>
        <div className="tu-search__emptyBody">
          Only some reports can be searched this way right now, and more are being added over time. You might have better luck searching by report name, year, or type.
          {hasActiveFilters && (
            <>
              {' '}
              <button type="button" className="tu-search__emptyLink" onClick={onClearAll}>
                Clear all filters
              </button>
            </>
          )}
          {' '}
          <button type="button" className="tu-search__emptyLink" onClick={onSwitchToMeta}>
            Switch to searching reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tu-search__contentList">
      {results.map((r) => {
        const govType = r.entity_government_type;
        const hasGovType = !!govType && govType !== 'NULL' && govType.trim() !== '';
        const remaining = Math.max(0, (r.total_matches ?? 0) - r.chunks.length);

        return (
          <div key={r.salesforce_report_document_id} className="tu-search__contentCard">
            <div className="tu-search__contentHead">
              <div className="tu-search__contentHeadMain">
                <div className="tu-search__contentEntity">{displayText(r.entity_name)}</div>
                <div className="tu-search__contentMeta">
                  {hasGovType && (
                    <span className={`tu-search__govChip tu-search__govChip--${getGovTypeColor(govType)}`}>
                      {govType}
                    </span>
                  )}
                  <span className="tu-search__contentMetaText">
                    {displayText(r.report_type_standardized)}
                  </span>
                  {r.report_year != null && (
                    <span className="tu-search__contentMetaText">· {r.report_year}</span>
                  )}
                </div>
              </div>
              {r.public_url && (
                <a
                  href={r.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tu-link tu-search__contentDownload"
                >
                  <i className="fas fa-download" aria-hidden="true"></i>
                  &nbsp;&nbsp;Download
                </a>
              )}
            </div>

            <div className="tu-search__snippets">
              {r.chunks.map((chunk) => {
                const windowed = makeSnippetWindow(chunk.chunk_text || '', highlightTerms);
                return (
                  <div key={chunk.chunk_id} className="tu-search__snippet">
                    <HighlightedText text={windowed} terms={highlightTerms} />
                  </div>
                );
              })}
            </div>

            {remaining > 0 && (
              <div className="tu-search__alsoAppears">
                Also appears {remaining} more {remaining === 1 ? 'time' : 'times'} in this report
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SearchReports;
